import { useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Upload, X, ArrowLeftRight, LayoutGrid, Image as ImageIcon, Play, Wand2 } from "lucide-react";

/**
 * Start / End Frame picker — used across every video generation section (T2V, I2V, V2V).
 *
 * Each slot supports:
 *   - drag & drop of an image file
 *   - click to open the file picker
 *   - "From Gallery" button to pick from user's existing images
 * A center swap button flips the two.
 *
 * Props:
 *   startFrame / endFrame       : {url, generation_id?} | null
 *   onChangeStart / onChangeEnd : (frame|null) => void
 *   testIdPrefix                : namespace for data-testids (default "frame")
 */
export function StartEndFramePicker({
  startFrame,
  endFrame,
  onChangeStart,
  onChangeEnd,
  testIdPrefix = "frame",
}) {
  const [picking, setPicking] = useState(null); // "start" | "end" | null
  const [loadingGallery, setLoadingGallery] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [uploading, setUploading] = useState(null); // "start" | "end" | null

  const openGallery = async (which) => {
    setPicking(which);
    if (gallery.length === 0) {
      setLoadingGallery(true);
      try {
        const { data } = await api.get("/generations?scope=mine");
        setGallery((data || []).filter((g) => g.image_url && !g.video_url));
      } catch {
        toast.error("Failed to load gallery");
      } finally {
        setLoadingGallery(false);
      }
    }
  };

  const applyPick = (g) => {
    const frame = { url: g.image_url, generation_id: g.generation_id };
    if (picking === "start") onChangeStart(frame);
    else if (picking === "end") onChangeEnd(frame);
    setPicking(null);
    toast.success(picking === "start" ? "Start frame pinned." : "End frame pinned.");
  };

  const uploadFile = async (which, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Only image files allowed."); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("Image must be under 15MB."); return; }
    setUploading(which);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("note", `${which} frame`);
      const { data } = await api.post("/upload/image", form, { headers: { "Content-Type": "multipart/form-data" } });
      const frame = { url: data.image_url, generation_id: data.generation_id };
      if (which === "start") onChangeStart(frame);
      else onChangeEnd(frame);
      toast.success(`${which === "start" ? "Start" : "End"} frame uploaded.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(null);
    }
  };

  const swap = () => {
    onChangeStart(endFrame);
    onChangeEnd(startFrame);
  };

  return (
    <div className="p-6 rounded-2xl bg-[#120E15] border border-[#F2C94C]/20" data-testid={`${testIdPrefix}-picker`}>
      <div className="flex items-center justify-between mb-3">
        <label className="font-mono-hy text-[10px] text-[#F2C94C] flex items-center gap-1.5">
          <Play className="w-3 h-3" /> Start & End Frames
        </label>
        <span className="font-mono-hy text-[9px] text-white">Optional · pins the opening + closing beats</span>
      </div>

      <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center">
        <FrameSlot
          which="start"
          frame={startFrame}
          accent="cyan"
          uploading={uploading === "start"}
          onUpload={(f) => uploadFile("start", f)}
          onClear={() => onChangeStart(null)}
          onPickGallery={() => openGallery("start")}
          testId={`${testIdPrefix}-start`}
        />

        <button
          onClick={swap}
          disabled={!startFrame && !endFrame}
          data-testid={`${testIdPrefix}-swap-btn`}
          title="Swap start ↔ end"
          className="w-10 h-10 rounded-full border border-[#F2C94C]/40 text-[#F2C94C] hover:bg-[#F2C94C]/15 hover:border-[#F2C94C] hover:rotate-180 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:rotate-0 transition-all duration-300 flex items-center justify-center"
        >
          <ArrowLeftRight className="w-4 h-4" />
        </button>

        <FrameSlot
          which="end"
          frame={endFrame}
          accent="gold"
          uploading={uploading === "end"}
          onUpload={(f) => uploadFile("end", f)}
          onClear={() => onChangeEnd(null)}
          onPickGallery={() => openGallery("end")}
          testId={`${testIdPrefix}-end`}
        />
      </div>

      {/* Transition preview reel — a slow crossfade between the two frames so users can feel the animation before spending credits */}
      {startFrame?.url && endFrame?.url && (
        <FramePreviewReel
          startUrl={startFrame.url}
          endUrl={endFrame.url}
          testId={`${testIdPrefix}-preview-reel`}
        />
      )}

      {/* Gallery picker */}
      <Dialog open={picking != null} onOpenChange={(o) => { if (!o) setPicking(null); }}>
        <DialogContent className="max-w-4xl bg-[#120E15] border-[#F2C94C]/25 text-white">
          <DialogTitle className="sr-only">Pick a {picking} frame from gallery</DialogTitle>
          <div className="p-2">
            <div className="font-mono-hy text-[10px] text-[#F2C94C] mb-3">
              // Pick a {picking === "start" ? "START" : "END"} frame from your gallery
            </div>
            {loadingGallery ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#F2C94C]" /></div>
            ) : gallery.length === 0 ? (
              <div className="text-center py-12">
                <ImageIcon className="w-10 h-10 text-[#F2C94C]/40 mx-auto mb-2" />
                <div className="font-mono-hy text-[10px] text-white">You haven't created any images yet.</div>
              </div>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3 max-h-[60vh] overflow-y-auto" data-testid={`${testIdPrefix}-gallery-grid`}>
                {gallery.map((g) => (
                  <button
                    key={g.generation_id}
                    onClick={() => applyPick(g)}
                    data-testid={`${testIdPrefix}-gallery-pick-${g.generation_id}`}
                    className="group aspect-square rounded-xl overflow-hidden border border-[#F2C94C]/15 hover:border-[#F2C94C] transition-all"
                  >
                    <img src={g.image_url} alt={g.prompt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const FrameSlot = ({ which, frame, accent, uploading, onUpload, onClear, onPickGallery, testId }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const accentBorder = accent === "gold" ? "border-[#F2C94C]" : "border-[#00E5FF]";
  const accentText = accent === "gold" ? "text-[#F2C94C]" : "text-[#00E5FF]";
  const accentTextSoft = accent === "gold" ? "text-[#F2C94C]/70" : "text-[#00E5FF]/70";

  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) onUpload(f);
  };

  const label = which === "start" ? "START · Frame 1" : "END · Last Frame";

  return (
    <div
      className={`rounded-2xl border-2 aspect-square relative overflow-hidden transition-all ${
        dragOver ? `${accentBorder} shadow-[0_0_18px_-4px_rgba(0,229,255,0.5)]` : frame ? `${accentBorder}/50` : `border-dashed ${accentBorder}/25`
      }`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      data-testid={`${testId}-slot`}
    >
      <div className={`absolute top-2 left-2 font-mono-hy text-[8px] px-2 py-0.5 rounded-full bg-[#0A0A0A]/85 border ${accentBorder}/40 ${accentText} tracking-widest z-10`}>
        {label}
      </div>
      {frame?.url ? (
        <>
          <img src={frame.url} alt={which} className="w-full h-full object-cover" data-testid={`${testId}-image`} />
          <button
            onClick={onClear}
            data-testid={`${testId}-clear`}
            className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#0A0A0A]/85 border border-[#E27D60]/40 text-[#E27D60] hover:bg-[#E27D60] hover:text-white flex items-center justify-center transition-all"
          >
            <X className="w-3 h-3" />
          </button>
        </>
      ) : (
        <div className="w-full h-full grid grid-rows-2 divide-y divide-[#00E5FF]/10 mt-4">
          <button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid={`${testId}-upload`}
            className={`w-full flex flex-col items-center justify-center gap-1 ${accentTextSoft} hover:${accentText} hover:bg-[#00E5FF]/5 transition-all disabled:opacity-50`}
          >
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
            <span className="font-mono-hy text-[9px] tracking-widest">UPLOAD</span>
          </button>
          <button
            onClick={onPickGallery}
            data-testid={`${testId}-gallery`}
            className={`w-full flex flex-col items-center justify-center gap-1 ${accentTextSoft} hover:${accentText} hover:bg-[#00E5FF]/5 transition-all`}
          >
            <LayoutGrid className="w-5 h-5" />
            <span className="font-mono-hy text-[9px] tracking-widest">GALLERY</span>
          </button>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid={`${testId}-input`}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onUpload(f);
          e.target.value = "";
        }}
      />
    </div>
  );
};

/**
 * FramePreviewReel — a slim CSS-crossfade preview between START and END frames.
 * Loops a 2.4s dissolve cycle so creators can feel the animation before spending credits.
 */
const FramePreviewReel = ({ startUrl, endUrl, testId }) => {
  return (
    <div className="mt-4 p-3 rounded-2xl bg-[#0A0A0A] border border-[#F2C94C]/30" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-mono-hy text-[10px] text-[#F2C94C] flex items-center gap-1.5">
          <Wand2 className="w-3 h-3" /> TRANSITION PREVIEW · 2.4s loop
        </div>
        <div className="font-mono-hy text-[9px] text-white">Rough dissolve — final render smooths via Seedance</div>
      </div>
      <div className="relative w-full aspect-video rounded-xl overflow-hidden border border-[#00E5FF]/25 bg-black" data-testid={`${testId}-stage`}>
        <img
          src={startUrl}
          alt="start"
          className="absolute inset-0 w-full h-full object-cover geneo-frame-fade-a"
          data-testid={`${testId}-start-img`}
        />
        <img
          src={endUrl}
          alt="end"
          className="absolute inset-0 w-full h-full object-cover geneo-frame-fade-b"
          data-testid={`${testId}-end-img`}
        />
        <div className="absolute bottom-2 left-2 font-mono-hy text-[8px] px-2 py-0.5 rounded-full bg-[#0A0A0A]/85 border border-[#00E5FF]/40 text-[#00E5FF] tracking-widest">START</div>
        <div className="absolute bottom-2 right-2 font-mono-hy text-[8px] px-2 py-0.5 rounded-full bg-[#0A0A0A]/85 border border-[#F2C94C]/40 text-[#F2C94C] tracking-widest">END</div>
      </div>
    </div>
  );
};

